import { Dispatch, SetStateAction, useState, useCallback } from 'react';

// credit to https://usehooks.com/useLocalStorage/ with minor tweaks
const useStorage = <T = any>(
    storage: Storage,
    key: string,
    initialValue: T
): [T, Dispatch<SetStateAction<T>>] => {
    // State to store our value
    // Pass initial state function to useState so logic is only executed once
    const [storedValue, setStoredValue] = useState<T>(() => {
        if (typeof storage === 'undefined') {
            return initialValue;
        }

        try {
            // Get from storage by key
            const item = storage.getItem(key);
            // Parse stored json or if none return initialValue
            return item ? JSON.parse(item) : initialValue;
        } catch (error) {
            // If error also return initialValue
            // eslint-disable-next-line no-console
            console.log(error);
            return initialValue;
        }
    });

    // Return a wrapped version of useState's setter function that ...
    // ... persists the new value to storage.
    const setValue: Dispatch<SetStateAction<T>> = useCallback(
        (value) => {
            try {
                // Allow value to be a function so we have same API as useState
                const valueToStore =
                    value instanceof Function ? value(storedValue) : value;
                // Save state
                setStoredValue(valueToStore);
                // Save to local storage
                if (typeof storage !== 'undefined') {
                    storage.setItem(key, JSON.stringify(valueToStore));
                }
            } catch (error) {
                // A more advanced implementation would handle the error case
                // eslint-disable-next-line no-console
                console.log(error);
            }
        },
        [storedValue, storage, key]
    );

    return [storedValue, setValue];
};

export const useLocalStorage = <T>(key: string, initialValue: T) =>
    useStorage(window?.localStorage, key, initialValue);

export const useSessionStorage = <T>(key: string, initialValue: T) =>
    useStorage(window?.sessionStorage, key, initialValue);
